const DISMISS_KEY = 'tesoreria_pwa_install_dismissed';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type Listener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;
let iosHelpOpen = false;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function initPwaRuntime() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try {
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* ignore */
    }
    notify();
  });

  window.addEventListener('online', notify);
  window.addEventListener('offline', notify);
}

export function subscribePwa(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setWaitingWorker(worker: ServiceWorker | null) {
  waitingWorker = worker;
  notify();
}

export function getWaitingWorker() {
  return waitingWorker;
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    nav.standalone === true
  );
}

export function isIosDevice() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iPadOs = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPadOs;
}

export function isInstallDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

export function dismissInstallPrompt() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  notify();
}

export async function promptInstall() {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return choice.outcome === 'accepted';
}

export function applyUpdate() {
  if (!waitingWorker) return;
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  waitingWorker = null;
  notify();
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}

export function canInstallNative() {
  return Boolean(deferredPrompt) && !isStandaloneDisplay();
}

export function canShowIosInstallHelp() {
  return isIosDevice() && !isStandaloneDisplay() && !isInstallDismissed();
}

export function requestIosInstallHelp() {
  iosHelpOpen = true;
  notify();
}

export function closeIosInstallHelp() {
  iosHelpOpen = false;
  notify();
}

export function isIosInstallHelpOpen() {
  return iosHelpOpen;
}
